import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Bell,
  ChevronRight,
  ImagePlus,
  KeyRound,
  LogOut,
  Mail,
  Settings as SettingsIcon,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";

import AvatarCropDialog from "@/components/AvatarCropDialog";
import MfaSettingsCard from "@/components/MfaSettingsCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchNotificationPreferences, saveNotificationPreferences } from "@/lib/notificationPreferences";
import { enableWebPushNotifications, getWebPushStatus, type WebPushStatus } from "@/lib/webPush";
import {
  buildNotificationPreferenceRecord,
  notificationPreferenceItems,
  type NotificationPreferenceKey,
  type NotificationPreferenceRecord,
} from "../../shared/system";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCheckbox,
  AppCardHeader,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppSegmentedControl,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";
import type { MemberCountryCode } from "../../shared/location";
import {
  DEFAULT_MEMBER_COUNTRY_CODE,
  getMemberCountryPickerOptions,
  isPausedMemberCountryCode,
  getMemberPostalCodeExample,
  getMemberPostalCodeLabel,
  getMemberPostalCodeValidationMessage,
  normalizeMemberCountryCode,
  normalizeMemberPostalCode,
  normalizeUsSchoolZip,
  normalizeUsStateOrTerritoryCode,
  normalizeUsSupportText,
} from "../../shared/location";
import {
  LOOKING_FOR_OPTIONS,
  PROFILE_AVAILABILITY_OPTIONS,
  PROFILE_CUSTODY_LABELS,
  PROFILE_CUSTODY_OPTIONS,
  PROFILE_INTEREST_OPTIONS,
} from "../../shared/community";

interface Profile {
  country_code: MemberCountryCode | null;
  display_name: string | null;
  postcode: string | null;
  support_us_state: string | null;
  support_us_county_or_city: string | null;
  support_us_school_district: string | null;
  support_us_school_zip: string | null;
  support_us_tribal_or_territory: string | null;
  bio: string | null;
  avatar_url: string | null;
  looking_for: string[] | null;
  interests: string[] | null;
  availability: string[] | null;
  custody_arrangement: string | null;
  created_at: string;
}

const buildFormFromProfile = (profile: Profile | null) => ({
  country_code: normalizeMemberCountryCode(profile?.country_code ?? DEFAULT_MEMBER_COUNTRY_CODE),
  display_name: profile?.display_name || "",
  postcode: profile?.postcode || "",
  support_us_state: normalizeUsStateOrTerritoryCode(profile?.support_us_state) ?? profile?.support_us_state ?? "",
  support_us_county_or_city: profile?.support_us_county_or_city || "",
  support_us_school_district: profile?.support_us_school_district || "",
  support_us_school_zip: profile?.support_us_school_zip || "",
  support_us_tribal_or_territory: profile?.support_us_tribal_or_territory || "",
  bio: profile?.bio || "",
  looking_for: profile?.looking_for || [],
  interests: profile?.interests || [],
  availability: profile?.availability || [],
  custody_arrangement: profile?.custody_arrangement || "",
});

const Settings = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [sendingReset, setSendingReset] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const [pushStatus, setPushStatus] = useState<WebPushStatus>("checking");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState(buildNotificationPreferenceRecord());
  const [savingNotificationKey, setSavingNotificationKey] = useState<NotificationPreferenceKey | null>(null);
  const [avatarDraftUrl, setAvatarDraftUrl] = useState<string | null>(null);
  const [form, setForm] = useState(buildFormFromProfile(null));
  const [locationError, setLocationError] = useState("");
  const [usStateError, setUsStateError] = useState("");
  const [usSchoolZipError, setUsSchoolZipError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshPushStatus = async () => {
    setPushStatus(await getWebPushStatus());
  };

  const loadProfile = async () => {
    if (!user) {
      setProfile(null);
      setNotificationPreferences(buildNotificationPreferenceRecord());
      setForm(buildFormFromProfile(null));
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    const [{ data, error }, nextNotificationPreferences] = await Promise.all([
      supabase
        .from("my_private_profile")
        .select("display_name, postcode, country_code, support_us_state, support_us_county_or_city, support_us_school_district, support_us_school_zip, support_us_tribal_or_territory, bio, avatar_url, looking_for, interests, availability, custody_arrangement, created_at")
        .single(),
      fetchNotificationPreferences(user.id),
    ]);

    setNotificationPreferences(nextNotificationPreferences);

    if (error) {
      toast.error("Couldn't load your profile just now.");
      setProfileLoading(false);
      return;
    }

    const nextProfile = data as Profile;
    setProfile(nextProfile);
    setForm(buildFormFromProfile(nextProfile));
    setLocationError("");
    setUsStateError("");
    setUsSchoolZipError("");
    setProfileLoading(false);
  };

  const clearAvatarDraft = () => {
    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }
    setAvatarDraftUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    void refreshPushStatus();
    void loadProfile();
  }, [user?.id]);

  useEffect(() => () => {
    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }
  }, [avatarDraftUrl]);

  const resetProfileForm = () => {
    setForm(buildFormFromProfile(profile));
    setLocationError("");
    setUsStateError("");
    setUsSchoolZipError("");
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      toast.error("We couldn't find an email address for this account.");
      return;
    }

    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    setSendingReset(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success(`We emailed a reset link to ${user.email}. Open it when you're ready and we'll help you set a new password.`);
  };

  const handleEnablePush = async () => {
    if (!user?.id) {
      toast.error("Please sign in again to manage notification settings.");
      return;
    }

    setEnablingPush(true);
    try {
      const nextStatus = await enableWebPushNotifications(user.id);
      setPushStatus(nextStatus);

      if (nextStatus === "enabled") {
        toast.success("Browser notifications are ready.");
      } else if (nextStatus === "denied") {
        toast.error("This browser is still blocking notifications.");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't turn on notifications right now.";
      toast.error(message);
    } finally {
      setEnablingPush(false);
      void refreshPushStatus();
    }
  };

  const updateNotificationPreferences = async (
    patch: Partial<NotificationPreferenceRecord>,
    savingKey: NotificationPreferenceKey,
  ) => {
    if (!user?.id) {
      toast.error("Please sign in again to manage notification settings.");
      return;
    }

    const previous = notificationPreferences;
    const optimistic = { ...previous, ...patch };
    setNotificationPreferences(optimistic);
    setSavingNotificationKey(savingKey);

    const { data, error } = await saveNotificationPreferences(user.id, patch);

    if (error) {
      setNotificationPreferences(previous);
      toast.error("Couldn't save those notification changes.");
      setSavingNotificationKey(null);
      return;
    }

    setNotificationPreferences(data ?? optimistic);
    setSavingNotificationKey(null);
  };

  const handleSaveProfile = async () => {
    if (!user) {
      return;
    }

    const normalizedPostcode = normalizeMemberPostalCode(form.country_code, form.postcode);
    if (form.postcode.trim() && !normalizedPostcode) {
      setLocationError(getMemberPostalCodeValidationMessage(form.country_code));
      toast.error("Please check your location format and try again.");
      return;
    }

    const normalizedUsState =
      form.country_code === "US" ? normalizeUsStateOrTerritoryCode(form.support_us_state) : null;
    if (form.country_code === "US" && !normalizedUsState) {
      setUsStateError("Please add your state or territory for U.S. support routing.");
      toast.error("Please add your U.S. state or territory before saving.");
      return;
    }

    const normalizedUsSchoolZip = form.support_us_school_zip.trim()
      ? normalizeUsSchoolZip(form.support_us_school_zip)
      : null;
    if (form.country_code === "US" && form.support_us_school_zip.trim() && !normalizedUsSchoolZip) {
      setUsSchoolZipError("Please enter a valid U.S. school ZIP code.");
      toast.error("Please check the school ZIP code and try again.");
      return;
    }

    setLocationError("");
    setUsStateError("");
    setUsSchoolZipError("");
    setSavingProfile(true);
    const nextProfile = {
      country_code: form.country_code,
      display_name: form.display_name.trim(),
      postcode: normalizedPostcode,
      support_us_state: form.country_code === "US" ? normalizedUsState : null,
      support_us_county_or_city: form.country_code === "US" ? normalizeUsSupportText(form.support_us_county_or_city) : null,
      support_us_school_district: form.country_code === "US" ? normalizeUsSupportText(form.support_us_school_district) : null,
      support_us_school_zip: form.country_code === "US" ? normalizedUsSchoolZip : null,
      support_us_tribal_or_territory: form.country_code === "US" ? normalizeUsSupportText(form.support_us_tribal_or_territory) : null,
      bio: form.bio.trim() || null,
      looking_for: form.looking_for,
      interests: form.interests,
      availability: form.availability,
      custody_arrangement: form.custody_arrangement || null,
    };

    const { error } = await supabase
      .from("profiles")
      .update(nextProfile)
      .eq("id", user.id);

    setSavingProfile(false);

    if (error) {
      toast.error("Couldn't save your profile. Please try again.");
      return;
    }

    setProfile((current) => current ? { ...current, ...nextProfile } : current);
    setForm((current) => ({
      ...current,
      country_code: nextProfile.country_code,
      display_name: nextProfile.display_name,
      postcode: nextProfile.postcode || "",
      support_us_state: nextProfile.support_us_state || "",
      support_us_county_or_city: nextProfile.support_us_county_or_city || "",
      support_us_school_district: nextProfile.support_us_school_district || "",
      support_us_school_zip: nextProfile.support_us_school_zip || "",
      support_us_tribal_or_territory: nextProfile.support_us_tribal_or_territory || "",
      bio: nextProfile.bio || "",
      looking_for: nextProfile.looking_for,
      interests: nextProfile.interests,
      availability: nextProfile.availability,
      custody_arrangement: nextProfile.custody_arrangement || "",
    }));
    toast.success("Profile updated!");
  };

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) {
      return;
    }

    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }

    setAvatarDraftUrl(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async (croppedBlob: Blob) => {
    if (!user) {
      return;
    }

    setUploadingPhoto(true);
    try {
      const filePath = `${user.id}/avatar.jpg`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, croppedBlob, { upsert: true, contentType: "image/jpeg" });

      if (uploadError) {
        throw uploadError;
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update({ avatar_url: filePath })
        .eq("id", user.id);

      if (profileError) {
        throw profileError;
      }

      setProfile((current) => current ? { ...current, avatar_url: `${filePath}?v=${Date.now()}` } : current);
      clearAvatarDraft();
      toast.success("Photo updated!");
    } catch {
      toast.error("Couldn't upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const toggleLookingFor = (value: string) => {
    setForm((current) => ({
      ...current,
      looking_for: current.looking_for.includes(value)
        ? current.looking_for.filter((item) => item !== value)
        : [...current.looking_for, value],
    }));
  };

  const toggleInterest = (value: string) => {
    setForm((current) => ({
      ...current,
      interests: current.interests.includes(value)
        ? current.interests.filter((item) => item !== value)
        : [...current.interests, value],
    }));
  };

  const toggleAvailability = (value: string) => {
    setForm((current) => ({
      ...current,
      availability: current.availability.includes(value)
        ? current.availability.filter((item) => item !== value)
        : [...current.availability, value],
    }));
  };

  const pushSubtitle = {
    checking: "Checking whether this browser is ready for Kindred updates.",
    unsupported: "This browser cannot show Kindred notifications.",
    disabled: "Off for this browser. Turn them on any time if you'd like gentle alerts.",
    denied: "Turned off in browser settings.",
    enabled: "Ready on this browser.",
  } satisfies Record<WebPushStatus, string>;

  const pushPreferenceSubtitle = notificationPreferences.push_enabled
    ? "Kindred can send the update types you keep switched on below."
    : "Paused in Kindred. Browser permission stays ready if you want to turn them back on.";

  if (profileLoading) {
    return <AppLoadingState title="Loading your settings" body="Pulling together your profile and account details." />;
  }

  const memberSince = profile?.created_at ? format(new Date(profile.created_at), "MMMM yyyy") : "";
  const postcodeLabel = getMemberPostalCodeLabel(form.country_code);
  const postcodeExample = getMemberPostalCodeExample(form.country_code);
  const countryHelper = isPausedMemberCountryCode(form.country_code)
    ? "Canada is currently paused while Kindred prepares a proper Quebec/French public, legal, and support layer."
    : "This keeps your private location format right wherever you use Kindred.";

  return (
    <AppSection pt="$sm" pb="$lg" gap="$md">
      <AppButton tone="ghost" size="sm" alignSelf="flex-start" onPress={() => navigate("/my-kindred")}>
        ← Back to Menu
      </AppButton>

      <AppCard>
        <AppCardBody gap="$sm">
          <XStack alignItems="center" gap="$sm">
            <XStack
              width={44}
              height={44}
              alignItems="center"
              justifyContent="center"
              borderRadius="$pill"
              backgroundColor="$primarySoft"
              borderWidth={1}
              borderColor="$primarySoft"
              flexShrink={0}
            >
              <SettingsIcon size={18} className="text-primary" />
            </XStack>
            <YStack minWidth={0} flex={1} gap={4}>
              <AppText color="$primary" fontSize="$xs" lineHeight="$xs" textTransform="uppercase" letterSpacing={1.2}>
                Account
              </AppText>
              <AppText fontFamily={kindredFontFamilies.display} fontSize="$heroSm" lineHeight="$heroSm" color="$heading">
                Settings
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                Profile, email, password, notifications, and the account actions that keep your Kindred login under control.
              </AppText>
            </YStack>
          </XStack>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title="Profile" />
        </AppCardHeader>
        <AppCardBody gap="$md">
          <AppCard backgroundColor="$background">
            <AppCardBody gap="$sm">
              <XStack
                flexDirection="column"
                gap="$md"
                justifyContent="space-between"
                $gtSm={{ flexDirection: "row", alignItems: "center" }}
              >
                <XStack alignItems="center" gap="$sm" flex={1} minWidth={0}>
                  <Avatar className="h-[72px] w-[72px]">
                    {profile?.avatar_url ? <AvatarImage src={profile.avatar_url} alt="Profile photo" /> : null}
                    <AvatarFallback className="bg-primary/20 text-primary text-2xl font-semibold">
                      {(profile?.display_name || "?")[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <YStack flex={1} minWidth={0} gap={2}>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      {profile?.display_name || "Kindred member"}
                    </AppText>
                    {profile?.postcode ? (
                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                        {profile.postcode}
                      </AppText>
                    ) : null}
                    {memberSince ? (
                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                        Member since {memberSince}
                      </AppText>
                    ) : null}
                  </YStack>
                </XStack>

                <YStack flexShrink={0} minWidth={160}>
                  <AppButton
                    tone="outline"
                    size="sm"
                    onPress={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    <ImagePlus size={16} />
                    {uploadingPhoto ? "Saving..." : profile?.avatar_url ? "Replace photo" : "Add photo"}
                  </AppButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarSelect}
                  />
                </YStack>
              </XStack>
            </AppCardBody>
          </AppCard>

          <AppFormSection>
            <AppFieldGroup label={<label htmlFor="name">Display name</label>}>
              <AppInput
                id="name"
                value={form.display_name}
                onChangeText={(value) => setForm((current) => ({ ...current, display_name: value }))}
              />
            </AppFieldGroup>

            <AppFieldGroup label="Country" helper={countryHelper}>
              <AppSegmentedControl
                options={getMemberCountryPickerOptions(form.country_code).map((option) => ({ value: option.value, label: option.label }))}
                value={form.country_code}
                onChange={(value) => {
                  setForm((current) => ({
                    ...current,
                    country_code: normalizeMemberCountryCode(value),
                  }));
                  setLocationError("");
                  setUsStateError("");
                  setUsSchoolZipError("");
                }}
                wrapItems
              />
            </AppFieldGroup>

            <AppFieldGroup
              label={<label htmlFor="postcode">{postcodeLabel}</label>}
              helper={`Example: ${postcodeExample}`}
              error={locationError}
            >
              <AppInput
                id="postcode"
                value={form.postcode}
                onChangeText={(value) => {
                  setForm((current) => ({ ...current, postcode: value }));
                  setLocationError("");
                }}
              />
            </AppFieldGroup>

            {form.country_code === "US" ? (
              <YStack gap="$sm">
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  Kindred keeps the USA as one support library. We start with the national explainer, then use these
                  details to hand you off to the right state, district, tribal, or local source when the next step varies.
                </AppText>

                <AppFieldGroup
                  label={<label htmlFor="us-state">State / territory</label>}
                  helper="Required for U.S. support routing. Use a two-letter code or full state / territory name."
                  error={usStateError}
                >
                  <AppInput
                    id="us-state"
                    value={form.support_us_state}
                    onChangeText={(value) => {
                      setForm((current) => ({ ...current, support_us_state: value }));
                      setUsStateError("");
                    }}
                    placeholder="e.g. CA or California"
                  />
                </AppFieldGroup>

                <AppFieldGroup
                  label={<label htmlFor="us-county-city">County or city</label>}
                  helper="Optional. Helpful for county or city-run housing, crisis, and hardship routes."
                >
                  <AppInput
                    id="us-county-city"
                    value={form.support_us_county_or_city}
                    onChangeText={(value) => setForm((current) => ({ ...current, support_us_county_or_city: value }))}
                    placeholder="e.g. Travis County or Austin"
                  />
                </AppFieldGroup>

                <AppFieldGroup
                  label={<label htmlFor="us-school-district">School district</label>}
                  helper="Optional. Helpful for district-led education and additional-needs routing."
                >
                  <AppInput
                    id="us-school-district"
                    value={form.support_us_school_district}
                    onChangeText={(value) => setForm((current) => ({ ...current, support_us_school_district: value }))}
                    placeholder="e.g. Austin ISD"
                  />
                </AppFieldGroup>

                <AppFieldGroup
                  label={<label htmlFor="us-school-zip">School ZIP</label>}
                  helper="Optional when district is unclear."
                  error={usSchoolZipError}
                >
                  <AppInput
                    id="us-school-zip"
                    value={form.support_us_school_zip}
                    onChangeText={(value) => {
                      setForm((current) => ({ ...current, support_us_school_zip: value }));
                      setUsSchoolZipError("");
                    }}
                    placeholder="e.g. 78704"
                  />
                </AppFieldGroup>

                <AppFieldGroup
                  label={<label htmlFor="us-tribal-territory">Tribal or territory pathway</label>}
                  helper="Optional when a tribal or territory route is the safer official handoff."
                >
                  <AppInput
                    id="us-tribal-territory"
                    value={form.support_us_tribal_or_territory}
                    onChangeText={(value) => setForm((current) => ({ ...current, support_us_tribal_or_territory: value }))}
                    placeholder="e.g. Navajo Nation or Puerto Rico"
                  />
                </AppFieldGroup>
              </YStack>
            ) : null}

            <AppFieldGroup label={<label htmlFor="bio">Bio</label>} helper={`${form.bio.length} / 280`}>
              <AppTextArea
                id="bio"
                value={form.bio}
                onChangeText={(value) => setForm((current) => ({ ...current, bio: value.slice(0, 280) }))}
                placeholder="Tell the Kindred community a little about yourself..."
                rows={3}
                maxLength={280}
              />
            </AppFieldGroup>

            <AppFieldGroup label="Looking for">
              <AppButtonRow>
                {LOOKING_FOR_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    type="button"
                    size="sm"
                    tone={form.looking_for.includes(option.value) ? "primary" : "outline"}
                    onPress={() => toggleLookingFor(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppFieldGroup>

            <AppFieldGroup label="Interests">
              <AppButtonRow>
                {PROFILE_INTEREST_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    type="button"
                    size="sm"
                    tone={form.interests.includes(option.value) ? "primary" : "outline"}
                    onPress={() => toggleInterest(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppFieldGroup>

            <AppFieldGroup label="Availability">
              <AppButtonRow>
                {PROFILE_AVAILABILITY_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    type="button"
                    size="sm"
                    tone={form.availability.includes(option.value) ? "primary" : "outline"}
                    onPress={() => toggleAvailability(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppFieldGroup>

            <AppFieldGroup
              label="Custody arrangement"
              helper={
                form.custody_arrangement
                  ? `Currently sharing: ${PROFILE_CUSTODY_LABELS[form.custody_arrangement] ?? form.custody_arrangement}`
                  : "Optional. This helps other members understand what family life looks like for you."
              }
            >
              <AppButtonRow>
                {PROFILE_CUSTODY_OPTIONS.map((option) => {
                  const active = form.custody_arrangement === option.value;
                  return (
                    <AppButton
                      key={option.value}
                      type="button"
                      size="sm"
                      tone={active ? "primary" : "outline"}
                      onPress={() =>
                        setForm((current) => ({
                          ...current,
                          custody_arrangement: active ? "" : option.value,
                        }))
                      }
                    >
                      {option.label}
                    </AppButton>
                  );
                })}
              </AppButtonRow>
            </AppFieldGroup>

            <AppButtonRow>
              <AppButton flex={1} onPress={handleSaveProfile} disabled={savingProfile}>
                {savingProfile ? "Saving..." : "Save profile"}
              </AppButton>
              <AppButton flex={1} tone="outline" onPress={resetProfileForm} disabled={savingProfile}>
                Reset
              </AppButton>
            </AppButtonRow>
          </AppFormSection>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title="Account access" />
        </AppCardHeader>
        <AppCardBody gap="$sm">
          <AppCard backgroundColor="$background">
            <AppCardBody>
              <XStack alignItems="flex-start" gap="$sm">
                <XStack
                  mt={2}
                  width={36}
                  height={36}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="$pill"
                  backgroundColor="$primarySoft"
                  borderWidth={1}
                  borderColor="$primarySoft"
                  flexShrink={0}
                >
                  <Mail size={16} className="text-primary" />
                </XStack>
                <YStack minWidth={0} flex={1} gap={2}>
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                    Email
                  </AppText>
                  <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                    {user?.email ?? "No email on this account"}
                  </AppText>
                </YStack>
              </XStack>
            </AppCardBody>
          </AppCard>

          <AppCard backgroundColor="$background">
            <AppCardBody>
              <XStack
                flexDirection="column"
                gap="$sm"
                justifyContent="space-between"
                $gtSm={{ flexDirection: "row", alignItems: "center" }}
              >
                <XStack alignItems="flex-start" gap="$sm" flex={1} minWidth={0}>
                  <XStack
                    mt={2}
                    width={36}
                    height={36}
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="$pill"
                    backgroundColor="$primarySoft"
                    borderWidth={1}
                    borderColor="$primarySoft"
                    flexShrink={0}
                  >
                    <KeyRound size={16} className="text-primary" />
                  </XStack>
                  <YStack minWidth={0} flex={1} gap={2}>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      Password
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      Send yourself a reset link so you can choose a new password securely.
                    </AppText>
                  </YStack>
                </XStack>

                <AppButton tone="outline" size="sm" onPress={handlePasswordReset} disabled={sendingReset}>
                  {sendingReset ? "Sending..." : "Send reset link"}
                </AppButton>
              </XStack>
            </AppCardBody>
          </AppCard>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title="Notifications" />
        </AppCardHeader>
        <AppCardBody gap="$sm">
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
            Keep the updates that matter on, and quiet the rest when you need less noise.
          </AppText>

          <AppCard backgroundColor="$background">
            <AppCardBody>
              <XStack
                flexDirection="column"
                gap="$sm"
                justifyContent="space-between"
                $gtSm={{ flexDirection: "row", alignItems: "center" }}
              >
                <XStack alignItems="flex-start" gap="$sm" flex={1} minWidth={0}>
                  <XStack
                    mt={2}
                    width={36}
                    height={36}
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="$pill"
                    backgroundColor="$primarySoft"
                    borderWidth={1}
                    borderColor="$primarySoft"
                    flexShrink={0}
                  >
                    <Bell size={16} className="text-primary" />
                  </XStack>
                  <YStack minWidth={0} flex={1} gap={2}>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      Browser permission
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      {pushSubtitle[pushStatus]}
                    </AppText>
                  </YStack>
                </XStack>

                {pushStatus === "disabled" ? (
                  <AppButton tone="outline" size="sm" onPress={handleEnablePush} disabled={enablingPush}>
                    {enablingPush ? "Turning on..." : "Turn on"}
                  </AppButton>
                ) : null}
              </XStack>
            </AppCardBody>
          </AppCard>

          <AppCard backgroundColor="$background">
            <AppCardBody>
              <AppCheckbox
                checked={notificationPreferences.push_enabled}
                disabled={Boolean(savingNotificationKey)}
                onCheckedChange={(checked) =>
                  void updateNotificationPreferences({ push_enabled: checked }, "push_enabled")
                }
                label={
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                    Allow Kindred browser alerts
                  </AppText>
                }
                description={pushPreferenceSubtitle}
                aria-label="Toggle Kindred push alerts"
              />
            </AppCardBody>
          </AppCard>

          <AppSection gap="$sm">
            {notificationPreferenceItems.map((item) => (
              <AppCard key={item.key} backgroundColor="$background">
                <AppCardBody>
                  <AppCheckbox
                    checked={notificationPreferences[item.key]}
                    disabled={Boolean(savingNotificationKey)}
                    onCheckedChange={(checked) =>
                      void updateNotificationPreferences(
                        { [item.key]: checked } as Partial<NotificationPreferenceRecord>,
                        item.key,
                      )
                    }
                    label={
                      <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                        {item.title}
                      </AppText>
                    }
                    description={item.description}
                    aria-label={`Toggle ${item.title.toLowerCase()}`}
                  />
                </AppCardBody>
              </AppCard>
            ))}
          </AppSection>
        </AppCardBody>
      </AppCard>

      <MfaSettingsCard />

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title="Manage account" />
        </AppCardHeader>
        <AppCardBody gap="$sm">
          <SettingsLinkRow
            icon={<Trash2 size={18} className="text-destructive" />}
            title="Delete account"
            subtitle="Request account deletion or data removal"
            onPress={() => navigate("/my-kindred/delete-account")}
          />

          <AppButton tone="outline" onPress={signOut}>
            <LogOut size={16} />
            Sign out
          </AppButton>
        </AppCardBody>
      </AppCard>

      <AvatarCropDialog
        open={Boolean(avatarDraftUrl)}
        imageSrc={avatarDraftUrl}
        saving={uploadingPhoto}
        onCancel={clearAvatarDraft}
        onConfirm={handleAvatarUpload}
      />
    </AppSection>
  );
};

const SettingsLinkRow = ({
  icon,
  onPress,
  subtitle,
  title,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onPress: () => void;
}) => (
  <button
    type="button"
    onClick={onPress}
    className="block w-full text-left transition hover:opacity-95"
  >
    <AppCard>
      <AppCardBody py="$sm">
        <XStack alignItems="center" gap="$sm">
          {icon}
          <YStack flex={1} minWidth={0} gap={2}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
              {title}
            </AppText>
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
              {subtitle}
            </AppText>
          </YStack>
          <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
        </XStack>
      </AppCardBody>
    </AppCard>
  </button>
);

export default Settings;
